import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/ui/button";
import { useNavigate } from "@tanstack/react-router";

import { boards$ } from "~/stores/boards";
import { createBoard } from "~/stores/boards/boards.actions";
import { datasets$ } from "~/stores/datasets";
import { datasources$ } from "~/stores/datasources";
import { getStartedChecklist$ } from "~/stores/get-started-checklist.store";
import { invitations$ } from "~/stores/invitations";
import { members$ } from "~/stores/members";

type StepId = "datasource" | "dataset" | "dashboard" | "invite";

interface Step {
  id: StepId;
  label: string;
  done: boolean;
}

/** Sidebar checklist that guides users through initial workspace setup. */
export const GetStartedChecklist = () => {
  const navigate = useNavigate();

  const dismissed = useValue(getStartedChecklist$.dismissed);

  const steps = useValue<Step[]>(() => [
    {
      id: "datasource",
      label: "Add a datasource",
      done: Object.keys(datasources$.get() ?? {}).length > 0,
    },
    {
      id: "dataset",
      label: "Create a dataset",
      done: Object.keys(datasets$.get() ?? {}).length > 0,
    },
    {
      id: "dashboard",
      label: "Create a dashboard",
      done: Object.keys(boards$.get() ?? {}).length > 0,
    },
    {
      id: "invite",
      label: "Invite member",
      done:
        (members$.get()?.length ?? 0) > 1 ||
        (invitations$.get()?.length ?? 0) > 0,
    },
  ]);

  const allComplete = steps.every((s) => s.done);

  if (dismissed || allComplete) return null;

  const requiredComplete = steps
    .filter((s) => s.id !== "invite")
    .every((s) => s.done);

  const firstIncompleteId = steps.find((s) => !s.done)?.id;

  const handleClick = (step: Step) => {
    if (step.done) {
      const routes: Record<StepId, string> = {
        datasource: "/settings/datasources",
        dataset: "/datasets",
        dashboard: "/boards",
        invite: "/settings/members",
      };
      navigate({ to: routes[step.id] });
      return;
    }

    switch (step.id) {
      case "datasource":
        navigate({ to: "/settings/datasources/new" });
        break;
      case "dataset":
        navigate({ to: "/datasets" });
        break;
      case "dashboard":
        createBoard();
        break;
      case "invite":
        navigate({ to: "/settings/members" });
        break;
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex h-8 items-center justify-between px-2">
        <span className="select-none font-medium text-muted-foreground text-xs">
          Get started
        </span>
        {requiredComplete && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => getStartedChecklist$.dismissed.set(true)}
          >
            <Icon name="cross" />
          </Button>
        )}
      </div>
      <div className="flex flex-col px-2">
        {steps.map((step, i) => {
          const isActive = step.id === firstIncompleteId;
          const isLast = i === steps.length - 1;

          return (
            <button
              key={step.id}
              type="button"
              className="group relative flex items-start gap-2 py-1 text-left"
              onClick={() => handleClick(step)}
            >
              {/* Connecting line */}
              {!isLast && (
                <div className="absolute top-[22px] left-[6px] h-[calc(100%-18px)] w-[2px] bg-border" />
              )}

              {/* Status indicator */}
              <div className="relative z-10 mt-0.5 flex size-3.5 shrink-0 items-center justify-center">
                {step.done && isActive ? (
                  <Icon
                    name="progress-filled"
                    className="size-3.5 text-green-800 dark:text-green-300"
                  />
                ) : step.done ? (
                  <Icon
                    name="progress-filled"
                    className="size-3.5 text-muted-foreground/30"
                  />
                ) : isActive ? (
                  <Icon
                    name="placeholder"
                    className="size-3.5 text-muted-foreground/60"
                  />
                ) : (
                  <Icon
                    name="placeholder"
                    className="size-3.5 text-muted-foreground/30"
                  />
                )}
              </div>

              {/* Label */}
              <span
                className={cn(
                  "text-xs",
                  isActive && !step.done
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
